import { useEffect, useState } from "react";
import { Receipt, Loader2, Crown, FileText, CheckCircle2, Clock, Ban, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { api } from "@/lib/api";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", LINE = "#E7E1D6", GREEN = "#1E7E45";
const HERO = { background: "radial-gradient(120% 140% at 85% 0%, rgba(216,154,0,.22), transparent 55%), var(--f-navy)", borderRadius: 16, padding: "24px 28px", color: "#F7F5F0" };
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "–");
const chf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;

const INV_STATUS = {
  paid: { key: "inv_paid", color: GREEN, bg: "rgba(30,126,69,.10)", icon: CheckCircle2 },
  open: { key: "inv_open", color: "#B7791F", bg: "rgba(216,154,0,.14)", icon: Clock },
  overdue: { key: "inv_overdue", color: "#DC2626", bg: "rgba(220,38,38,.10)", icon: Clock },
  draft: { key: "inv_draft", color: SLATE, bg: "rgba(119,127,136,.12)", icon: FileText },
  void: { key: "inv_void", color: "#9CA3AF", bg: "rgba(156,163,175,.12)", icon: Ban },
};

export default function AboRechnungen() {
  const { t } = useTranslation();
  const [b, setB] = useState(null);
  const [open, setOpen] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subErr, setSubErr] = useState("");

  const load = () => api.get("/billing").then(({ data }) => setB(data)).catch(() => setB(false));
  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    setSubErr(""); setSubscribing(true);
    try {
      const { data } = await api.post("/billing/subscribe", { plan: b.plan, confirm: true });
      setB(data); setConfirm(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      setSubErr((typeof d === "string" ? d : "Abo konnte nicht aktiviert werden."));
    } finally { setSubscribing(false); }
  };

  if (b === null) return <CockpitLayout active="abo" title={t("billing.title")}><div className="flex justify-center py-20"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div></CockpitLayout>;
  if (b === false) return <CockpitLayout active="abo" title={t("billing.title")}><div className="py-16 text-center text-[14px]" style={{ color: SLATE }} data-testid="billing-error">{t("billing.load_error")}</div></CockpitLayout>;

  const sub = b.subscription || {};
  const ent = b.entitlements || {};
  const trial = b.trial || {};
  const invoices = b.invoices || [];
  const subStatusLabel = { active: t("billing.sub_active"), trialing: t("billing.sub_trialing"), cancelled: t("billing.sub_cancelled"), past_due: t("billing.sub_past_due") };
  const TRIAL_BADGE = {
    prepared: { color: SLATE, bg: "rgba(119,127,136,.18)" },
    trial_running: { color: GOLD, bg: "rgba(216,154,0,.2)" },
    trial_ended: { color: "#DC2626", bg: "rgba(220,38,38,.14)" },
    subscription_active: { color: GREEN, bg: "rgba(30,126,69,.18)" },
  };
  const tb = TRIAL_BADGE[trial.status] || TRIAL_BADGE.prepared;
  const isFree = b.plan === "FREE";
  const heroBadge = isFree
    ? { color: "#C9CCD2", bg: "rgba(119,127,136,.28)", label: "Kostenlos" }
    : { color: tb.color, bg: tb.bg, label: trial.status_label || subStatusLabel[sub.status] || sub.status };
  const statusLabel = isFree ? "Kostenlos" : (trial.status_label || "–");

  return (
    <CockpitLayout active="abo" title={t("billing.title")}>
      <div className="fundio-page-stack" data-testid="gold-abo">
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title flex items-center gap-2.5" data-testid="billing-title">
              <Receipt size={28} style={{ color: GOLD }} /> {t("billing.title")}
            </h1>
            <p className="mt-1 fundio-muted text-[14px]">{t("billing.subtitle")}</p>
          </div>
        </div>

        {/* subscription hero */}
        <div className="flex flex-wrap items-center justify-between gap-4" style={HERO} data-testid="billing-hero">
          <div>
            <div className="flex items-center gap-2">
              <Crown size={18} style={{ color: GOLD }} />
              <span className="text-[22px] font-black" data-testid="billing-plan">{b.plan}</span>
              <span className="rounded-full px-2.5 py-0.5 text-[11px] font-bold" style={{ background: heroBadge.bg, color: heroBadge.color }} data-testid="billing-trial-status">{heroBadge.label}</span>
            </div>
            <p className="mt-1 text-[14px]" style={{ color: "#C9CCD2" }} data-testid="billing-price">{t("billing.per_month", { amount: chf(ent.price_monthly_chf) })}</p>
            {trial.status === "trial_running" && trial.ends_at && (
              <p className="mt-2 text-[12px]" style={{ color: GOLD }} data-testid="billing-trial">Testphase läuft bis {fmtDate(trial.ends_at)} · noch {trial.days_left} Tage</p>
            )}
            {trial.status === "trial_ended" && (
              <p className="mt-2 text-[12px]" style={{ color: "#F3B9B9" }} data-testid="billing-trial">Testphase beendet am {fmtDate(trial.ends_at)}. Der kostenlose Verzeichniseintrag bleibt erhalten.</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wide" style={{ color: "#9AA0A8" }}>{t("billing.since")}</p>
            <p className="text-[15px] font-semibold">{fmtDate(sub.started_at)}</p>
          </div>
        </div>

        {/* Abo & Testphase */}
        <div className="fundio-panel" data-testid="billing-trial-card">
          <h2 className="flex items-center gap-2 text-[17px] font-bold" style={{ color: INK }}>
            <Clock size={16} style={{ color: GOLD }} /> Abo &amp; Testphase
          </h2>
          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 text-[13.5px] sm:grid-cols-4">
            <div><p className="text-[11px] uppercase tracking-wide" style={{ color: SLATE }}>{isFree ? "Paket" : "Testpaket"}</p><p className="font-semibold" style={{ color: INK }} data-testid="trial-package">{b.plan}</p></div>
            <div><p className="text-[11px] uppercase tracking-wide" style={{ color: SLATE }}>Status</p><p className="font-semibold" style={{ color: isFree ? SLATE : tb.color }} data-testid="trial-status-label">{statusLabel}</p></div>
            <div><p className="text-[11px] uppercase tracking-wide" style={{ color: SLATE }}>Beginn</p><p className="font-semibold" style={{ color: INK }} data-testid="trial-start">{trial.started_at ? fmtDate(trial.started_at) : "Noch nicht gestartet"}</p></div>
            <div><p className="text-[11px] uppercase tracking-wide" style={{ color: SLATE }}>Ende</p><p className="font-semibold" style={{ color: INK }} data-testid="trial-end">{trial.ends_at ? fmtDate(trial.ends_at) : "–"}</p></div>
          </div>
          {trial.status === "trial_running" && (
            <p className="mt-3 text-[12.5px]" style={{ color: MUTED }} data-testid="trial-days-left">Verbleibend: <span className="font-bold" style={{ color: GOLD }}>{trial.days_left} Tage</span>. Während der kostenlosen Testphase werden keine Rechnungen erstellt.</p>
          )}
          {trial.status === "prepared" && (
            <p className="mt-3 text-[12.5px]" style={{ color: MUTED }} data-testid="trial-prepared-note">Die Testphase startet automatisch, sobald der eigene Zugang erfolgreich aktiviert wurde.</p>
          )}
        </div>

        {/* Kostenpflichtiges Abo auswählen & bestätigen (Kunde) */}
        {["LIGHT", "WEBPRO", "MULTI"].includes(b.plan) && sub.status !== "active" && (
          <div className="fundio-panel" data-testid="subscribe-card">
            <h2 className="flex items-center gap-2 text-[17px] font-bold" style={{ color: INK }}>
              <Crown size={16} style={{ color: GOLD }} /> Kostenpflichtiges Abo
            </h2>
            <p className="mt-2 text-[13.5px]" style={{ color: MUTED }}>
              Paket <span className="font-bold" style={{ color: INK }}>{b.plan}</span> · {chf(ent.price_monthly_chf)} / Monat.
              {trial.status === "trial_ended"
                ? " Deine Testphase ist beendet – ohne Abo bleibt der kostenlose Verzeichniseintrag erhalten, die Premium-Funktionen sind pausiert."
                : " Wähle jetzt verbindlich dein kostenpflichtiges Abo, damit die Premium-Funktionen nach der Testphase aktiv bleiben."}
            </p>
            <label className="mt-3 flex items-start gap-2.5 text-[13px]" style={{ color: MUTED }}>
              <input type="checkbox" data-testid="subscribe-confirm" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-0.5" />
              <span>Ich wähle das kostenpflichtige Abo {b.plan} ({chf(ent.price_monthly_chf)}/Monat) verbindlich aus. Eine Rechnung entsteht erst nach dieser Bestätigung.</span>
            </label>
            {subErr && <p className="mt-2 text-[12.5px] text-red-600" data-testid="subscribe-error">{subErr}</p>}
            <button
              data-testid="subscribe-submit"
              disabled={!confirm || subscribing}
              onClick={subscribe}
              className="fundio-action fundio-action--primary mt-3"
            >
              {subscribing && <Loader2 size={15} className="animate-spin" />} Abo verbindlich aktivieren
            </button>
          </div>
        )}

        {/* current line items */}
        <div className="fundio-panel" data-testid="billing-line-items">
          <h2 className="text-[17px] font-bold" style={{ color: INK }}>{t("billing.positions")}</h2>
          <div className="mt-3 divide-y" style={{ borderColor: LINE }}>
            {(b.preview_line_items || []).map((li, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 text-[14px]" data-testid={`billing-line-${i}`}>
                <span style={{ color: MUTED }}>{li.description}</span>
                <span className="font-semibold tabular-nums" style={{ color: INK }}>{chf(li.amount_chf)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-3 text-[15px] font-black" style={{ color: INK }}>
              <span>{t("billing.total_month")}</span>
              <span className="tabular-nums" data-testid="billing-total">{chf((b.preview_line_items || []).reduce((a, li) => a + (li.amount_chf || 0), 0))}</span>
            </div>
          </div>
        </div>

        {/* payment note (honest — no provider) */}
        <div className="fundio-panel flex items-start gap-3" style={{ borderStyle: "dashed" }} data-testid="billing-payment-note">
          <ShieldCheck size={18} className="mt-0.5 shrink-0" style={{ color: GOLD }} />
          <p className="text-[13px] leading-relaxed" style={{ color: MUTED }}>
            {b.payment?.note || t("billing.payment_note")}
          </p>
        </div>

        {/* invoices */}
        <div className="fundio-panel" data-testid="billing-invoices">
          <h2 className="text-[17px] font-bold" style={{ color: INK }}>{t("billing.invoices")}</h2>
          {invoices.length === 0 ? (
            <p className="mt-3 text-[13.5px]" style={{ color: SLATE }} data-testid="billing-invoices-empty">{t("billing.invoices_empty")}</p>
          ) : (
            <div className="mt-4 space-y-2.5">
              {invoices.map((inv) => {
                const st = INV_STATUS[inv.status] || INV_STATUS.draft;
                const Icon = st.icon;
                const isOpen = open === inv.id;
                return (
                  <div key={inv.id} className="rounded-lg border" style={{ borderColor: LINE }} data-testid={`invoice-${inv.id}`}>
                    <button onClick={() => setOpen(isOpen ? null : inv.id)} data-testid={`invoice-toggle-${inv.id}`} className="flex w-full items-center justify-between gap-3 p-3.5 text-left hover:bg-fundio-subtle">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: st.bg }}><Icon size={16} style={{ color: st.color }} /></span>
                        <div>
                          <p className="text-[14px] font-bold" style={{ color: INK }}>{inv.number}</p>
                          <p className="text-[12px]" style={{ color: SLATE }}>{t("billing.issued", { date: fmtDate(inv.issued_at) })}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[14px] font-black tabular-nums" style={{ color: INK }}>{chf(inv.total)}</p>
                        <span className="text-[11px] font-bold uppercase" style={{ color: st.color }} data-testid={`invoice-status-${inv.id}`}>{t(`billing.${st.key}`)}</span>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t px-3.5 py-3" style={{ borderColor: LINE }} data-testid={`invoice-detail-${inv.id}`}>
                        <div className="grid grid-cols-2 gap-2 text-[12.5px]" style={{ color: SLATE }}>
                          <span>{t("billing.due", { date: fmtDate(inv.due_at) })}</span>
                          <span>{inv.paid_at ? t("billing.paid", { date: fmtDate(inv.paid_at) }) : t("billing.not_paid")}</span>
                        </div>
                        <div className="mt-3 divide-y" style={{ borderColor: LINE }}>
                          {(inv.line_items || []).map((li, i) => (
                            <div key={i} className="flex items-center justify-between py-2 text-[13px]">
                              <span style={{ color: MUTED }}>{li.description}</span>
                              <span className="font-semibold tabular-nums" style={{ color: INK }}>{chf(li.amount_chf)}</span>
                            </div>
                          ))}
                          <div className="flex items-center justify-between pt-2 text-[14px] font-black" style={{ color: INK }}>
                            <span>{t("billing.total")}</span><span className="tabular-nums">{chf(inv.total)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </CockpitLayout>
  );
}
